/* eslint-disable no-unused-vars */
/* eslint-disable no-template-curly-in-string */
import queryString from 'qs';
import { isFunction, omit, pick } from 'lodash';
import apiCore, { withAuth, TOKEN_STORAGE_KEY, parseQuery } from './core';
import { serializeFilters } from '../utils/filters';

const API_ROOT = process.env.REACT_APP_PORTAL_API;

const countryDemoQueries = [
  'actorCountryIndex',
  'networkCountryIndex',
  'showCountryFollowerIndex',
  'audienceShowViewCountry',
  'movieFollowerCountryIndex',
  'movieTrackerCountryIndex',
  'showCountryIndex',
  'movieCountryIndex'
];

export default {
  fetchLook: async (id, lookMeta, filters) => {
    const validFilters = pick(filters, lookMeta.fields);
    const params = queryString.stringify({
      filters: serializeFilters(validFilters),
      showTitle: !!lookMeta.showTitle
    });

    try {
      const url = `${API_ROOT}v1/embed/${id}?${params}`;
      const { data } = await withAuth(apiCore.get)(url);

      return { url: data.embed.url, blocked: false };
    } catch (err) {
      if (err.status === 403) {
        return { url: '', blocked: true };
      }

      return Promise.reject(err);
    }
  },

  querySteam: async (
    id,
    queryMeta,
    filters,
    resultFormat,
    { sorts, limit }
  ) => {
    const fs = filters;
    if (countryDemoQueries.includes(id)) {
      fs.viewers = {
        id: 'viewers',
        value: { value: '>=30' }
      };
    }
    const validFilters = pick(filters, queryMeta.fields);
    const params = queryString.stringify({
      filters: serializeFilters(validFilters, false),
      resultFormat,
      sorts,
      limit
    });

    try {
      const url = `${API_ROOT}v1/query-view/${id}?${params}`;
      const overrides = {};

      if (resultFormat && resultFormat !== 'json') {
        overrides.headers = {
          'Content-Type': 'application/json',
          Accept: 'application/octet-stream'
        };

        const resp = await withAuth(apiCore.get)(url, overrides);
        return resp;
      }

      return await withAuth(apiCore.get)(url);
    } catch (err) {
      return Promise.reject(err);
    }
  },

  runInlineQueryStream: async (id, queryMeta, filters, resultFormat, sorts) => {
    const fs = filters;
    if (countryDemoQueries.includes(id)) {
      fs.viewers = {
        id: 'viewers',
        value: { value: '>=30' }
      };
    }

    const validFilters = pick(filters, queryMeta.fields);
    const params = queryString.stringify({
      filters: serializeFilters(validFilters, false),
      resultFormat,
      sorts
    });
    try {
      const url = `${API_ROOT}v1/query-view/${id}?${params}`;
      const overrides = {};

      if (countryDemoQueries.includes(id)) {
        overrides.viewers = {
          id: 'viewers',
          value: { value: '>=30' }
        };
      }

      if (resultFormat) {
        overrides.headers = {
          'Content-Type': 'application/json',
          Accept: 'application/octet-stream'
        };

        return withAuth(apiCore.getStream)(url, overrides);
      }

      const { data } = await withAuth(apiCore.getStream)(url);
      return data;
    } catch (err) {
      return Promise.reject(err);
    }
  },

  runInlineQuery: async (id, queryMeta, filters, resultFormat) => {
    const { resultFormatter } = pick(queryMeta, ['resultFormatter']);
    queryMeta = omit(queryMeta, ['resultFormatter']);
    const validFilters = pick(filters, queryMeta.fields);

    const params = queryString.stringify({
      filters: serializeFilters(validFilters, false),
      resultFormat
    });

    try {
      const url = `${API_ROOT}v1/query/${id}?${params}`;
      const overrides = {};
      if (resultFormat && resultFormat !== 'json') {
        overrides.headers = {
          'Content-Type': 'application/json',
          Accept: 'application/octet-stream'
        };

        return withAuth(apiCore.get)(url, overrides);
      }

      const { data } = await withAuth(apiCore.get)(url);
      const resultDate = isFunction(resultFormatter)
        ? resultFormatter(data)
        : data;
      return resultDate;
    } catch (err) {
      return Promise.reject(err);
    }
  },

  /**
   * @param {number} queryId // id of query
   * @param {string} resultFormat // Format of result
   *
   * See: https://github.com/MadisonReed/node-looker-api/blob/master/docs/QueryApi.md#runQuery
   */

  runSavedQuery: (queryId, resultFormat) => {},

  fetchViews: async () => {
    const data = {
      data: [
        {
          fields: {
            dimensions: [
              {
                align: 'left',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: false,
                label: 'Audience Device Name',
                label_from_parameter: null,
                label_short: 'Device Name',
                map_layer: null,
                name: 'pdt_device_denorm.device_name',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'string',
                user_attribute_filter_types: [
                  'string',
                  'advanced_filter_string'
                ],
                value_format: null,
                view: 'pdt_device_denorm',
                view_label: 'Audience',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'pdt_device_denorm',
                dimension_group: null,
                error: null,
                field_group_variant: 'Device Name',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'pdt_device_denorm',
                suggest_dimension: 'pdt_device_denorm.device_name',
                suggest_explore: 'client_crosstab',
                suggestable: true,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/pdt_device_denorm.view.lkml?line=153',
                permanent: null,
                source_file: 'pdt_device_denorm.view.lkml',
                source_file_path: 'tvlytics/pdt_device_denorm.view.lkml',
                sql: '${TABLE}.device_name ',
                sql_case: null,
                filters: null,
                times_used: 283
              },
              {
                align: 'left',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: false,
                label: 'Audience User Age Bucket',
                label_from_parameter: null,
                label_short: 'User Age Bucket',
                map_layer: null,
                name: 'client_crosstab.user_age_bucket',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'string',
                user_attribute_filter_types: [
                  'string',
                  'advanced_filter_string'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Audience',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'User Age Bucket',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.user_age_bucket',
                suggest_explore: 'client_crosstab',
                suggestable: true,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=274',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.user_age_bucket ',
                sql_case: null,
                filters: null,
                times_used: 565
              },
              {
                align: 'left',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: false,
                label: 'Audience User Country',
                label_from_parameter: null,
                label_short: 'User Country',
                map_layer: null,
                name: 'client_crosstab.user_country',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'string',
                user_attribute_filter_types: [
                  'string',
                  'advanced_filter_string'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Audience',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'User Country',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.user_country',
                suggest_explore: 'client_crosstab',
                suggestable: true,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=268',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.user_country ',
                sql_case: null,
                filters: null,
                times_used: 643
              },
              {
                align: 'left',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: false,
                label: 'Audience User Gender',
                label_from_parameter: null,
                label_short: 'User Gender',
                map_layer: null,
                name: 'client_crosstab.user_gender',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'string',
                user_attribute_filter_types: [
                  'string',
                  'advanced_filter_string'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Audience',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'User Gender',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.user_gender',
                suggest_explore: 'client_crosstab',
                suggestable: true,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=262',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.user_gender ',
                sql_case: null,
                filters: null,
                times_used: 1426
              },
              {
                align: 'left',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: true,
                is_filter: false,
                is_numeric: false,
                label: 'Content Details Content ID',
                label_from_parameter: null,
                label_short: 'Content ID',
                map_layer: null,
                name: 'client_crosstab.content_id',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'string',
                user_attribute_filter_types: [
                  'string',
                  'advanced_filter_string'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Content Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Content ID',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.content_id',
                suggest_explore: 'client_crosstab',
                suggestable: true,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=143',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.content_id ',
                sql_case: null,
                filters: null,
                times_used: 23
              },
              {
                align: 'left',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: false,
                label: 'Content Details Content Type',
                label_from_parameter: null,
                label_short: 'Content Type',
                map_layer: null,
                name: 'client_crosstab.content_type',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'string',
                user_attribute_filter_types: [
                  'string',
                  'advanced_filter_string'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Content Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Content Type',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.content_type',
                suggest_explore: 'client_crosstab',
                suggestable: true,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=149',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.content_type ',
                sql_case: null,
                filters: null,
                times_used: 2135
              },
              {
                align: 'left',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: 'range',
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: false,
                label: 'Content Details Date',
                label_from_parameter: null,
                label_short: 'Date',
                map_layer: null,
                name: 'client_crosstab.date',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'date',
                user_attribute_filter_types: [
                  'datetime',
                  'advanced_filter_datetime'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Content Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Date',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.date',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: true,
                can_time_filter: false,
                time_interval: { name: 'day', count: 1 },
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=138',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.created_at ',
                sql_case: null,
                filters: null,
                times_used: 434
              },
              {
                align: 'right',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: true,
                label: 'Content Details Days from First Air Date',
                label_from_parameter: null,
                label_short: 'Days from First Air Date',
                map_layer: null,
                name: 'client_crosstab.days_from_release',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'number',
                user_attribute_filter_types: [
                  'number',
                  'advanced_filter_number'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Content Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Days from First Air Date',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.days_from_release',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=249',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.days_from_release ',
                sql_case: null,
                filters: null,
                times_used: 498
              },
              {
                align: 'right',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: true,
                is_filter: false,
                is_numeric: true,
                label: 'Content Details Episode ID',
                label_from_parameter: null,
                label_short: 'Episode ID',
                map_layer: null,
                name: 'client_crosstab.episode_id',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'number',
                user_attribute_filter_types: [
                  'number',
                  'advanced_filter_number'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Content Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Episode ID',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.episode_id',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=165',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.episode_id ',
                sql_case: null,
                filters: null,
                times_used: 0
              },
              {
                align: 'left',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: 'range',
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: false,
                label: 'Content Details First Release Date',
                label_from_parameter: null,
                label_short: 'First Release Date',
                map_layer: null,
                name: 'client_crosstab.first_release_date',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'date',
                user_attribute_filter_types: [
                  'datetime',
                  'advanced_filter_datetime'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Content Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'First Release Date',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.first_release_date',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: true,
                can_time_filter: false,
                time_interval: { name: 'day', count: 1 },
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=244',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.first_release_date ',
                sql_case: null,
                filters: null,
                times_used: 385
              },
              {
                align: 'left',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: true,
                is_filter: false,
                is_numeric: false,
                label: 'Content Details Follower Tier',
                label_from_parameter: null,
                label_short: 'Follower Tier',
                map_layer: null,
                name: 'client_crosstab.follower_tier',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'string',
                user_attribute_filter_types: [
                  'string',
                  'advanced_filter_string'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Content Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Follower Tier',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.follower_tier',
                suggest_explore: 'client_crosstab',
                suggestable: true,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=194',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.follower_tier ',
                sql_case: null,
                filters: null,
                times_used: 2
              },
              {
                align: 'left',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: false,
                label: 'Content Details Genre',
                label_from_parameter: null,
                label_short: 'Genre',
                map_layer: null,
                name: 'pdt_genre_unified.genre',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'string',
                user_attribute_filter_types: [
                  'string',
                  'advanced_filter_string'
                ],
                value_format: null,
                view: 'pdt_genre_unified',
                view_label: 'Content Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'pdt_genre_unified',
                dimension_group: null,
                error: null,
                field_group_variant: 'Genre',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'pdt_genre_unified',
                suggest_dimension: 'pdt_genre_unified.genre',
                suggest_explore: 'client_crosstab',
                suggestable: true,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/pdt_genre_unified.view.lkml?line=53',
                permanent: null,
                source_file: 'pdt_genre_unified.view.lkml',
                source_file_path: 'tvlytics/pdt_genre_unified.view.lkml',
                sql: '${TABLE}.genre ',
                sql_case: null,
                filters: null,
                times_used: 579
              },
              {
                align: 'left',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: false,
                label: 'Content Details Genre String',
                label_from_parameter: null,
                label_short: 'Genre String',
                map_layer: null,
                name: 'client_crosstab.genre_string',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'string',
                user_attribute_filter_types: [
                  'string',
                  'advanced_filter_string'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Content Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Genre String',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.genre_string',
                suggest_explore: 'client_crosstab',
                suggestable: true,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=206',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.genre ',
                sql_case: null,
                filters: null,
                times_used: 217
              },
              {
                align: 'right',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: true,
                is_filter: false,
                is_numeric: true,
                label: 'Content Details Network ID',
                label_from_parameter: null,
                label_short: 'Network ID',
                map_layer: null,
                name: 'client_crosstab.network_id',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'number',
                user_attribute_filter_types: [
                  'number',
                  'advanced_filter_number'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Content Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Network ID',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.network_id',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=227',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.network_id ',
                sql_case: null,
                filters: null,
                times_used: 0
              },
              {
                align: 'left',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: false,
                label: 'Content Details Originating Country',
                label_from_parameter: null,
                label_short: 'Originating Country',
                map_layer: null,
                name: 'client_crosstab.originating_country',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'string',
                user_attribute_filter_types: [
                  'string',
                  'advanced_filter_string'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Content Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Originating Country',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.originating_country',
                suggest_explore: 'client_crosstab',
                suggestable: true,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=211',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.originating_country ',
                sql_case: null,
                filters: null,
                times_used: 107
              },
              {
                align: 'left',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: false,
                label: 'Content Details Originating Language',
                label_from_parameter: null,
                label_short: 'Originating Language',
                map_layer: null,
                name: 'client_crosstab.originating_language',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'string',
                user_attribute_filter_types: [
                  'string',
                  'advanced_filter_string'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Content Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Originating Language',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.originating_language',
                suggest_explore: 'client_crosstab',
                suggestable: true,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=216',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.originating_language ',
                sql_case: null,
                filters: null,
                times_used: 80
              },
              {
                align: 'left',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: false,
                label: 'Content Details Originating Network',
                label_from_parameter: null,
                label_short: 'Originating Network',
                map_layer: null,
                name: 'client_crosstab.network_name',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'string',
                user_attribute_filter_types: [
                  'string',
                  'advanced_filter_string'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Content Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Originating Network',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.network_name',
                suggest_explore: 'client_crosstab',
                suggestable: true,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=221',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.network_name ',
                sql_case: null,
                filters: null,
                times_used: 7355
              },
              {
                align: 'left',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: false,
                label: 'Content Details Parent Content ID',
                label_from_parameter: null,
                label_short: 'Parent Content ID',
                map_layer: null,
                name: 'client_crosstab.parent_content_id',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'string',
                user_attribute_filter_types: [
                  'string',
                  'advanced_filter_string'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Content Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Parent Content ID',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.parent_content_id',
                suggest_explore: 'client_crosstab',
                suggestable: true,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=189',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.parent_content_id ',
                sql_case: null,
                filters: null,
                times_used: 4322
              },
              {
                align: 'right',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: true,
                label: 'Content Details Runtime',
                label_from_parameter: null,
                label_short: 'Runtime',
                map_layer: null,
                name: 'client_crosstab.runtime',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'number',
                user_attribute_filter_types: [
                  'number',
                  'advanced_filter_number'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Content Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Runtime',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.runtime',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=295',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.runtime ',
                sql_case: null,
                filters: null,
                times_used: 17
              },
              {
                align: 'left',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: false,
                label: 'Content Details Status',
                label_from_parameter: null,
                label_short: 'Status',
                map_layer: null,
                name: 'client_crosstab.status',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'string',
                user_attribute_filter_types: [
                  'string',
                  'advanced_filter_string'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Content Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Status',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.status',
                suggest_explore: 'client_crosstab',
                suggestable: true,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=300',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.status ',
                sql_case: null,
                filters: null,
                times_used: 55
              },
              {
                align: 'right',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: true,
                is_filter: false,
                is_numeric: true,
                label: 'Content Details Studio ID',
                label_from_parameter: null,
                label_short: 'Studio ID',
                map_layer: null,
                name: 'client_crosstab.studio_id',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'number',
                user_attribute_filter_types: [
                  'number',
                  'advanced_filter_number'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Content Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Studio ID',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.studio_id',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=238',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.studio_id ',
                sql_case: null,
                filters: null,
                times_used: 0
              },
              {
                align: 'left',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: false,
                label: 'Content Details Studio Name',
                label_from_parameter: null,
                label_short: 'Studio Name',
                map_layer: null,
                name: 'client_crosstab.studio_name',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'string',
                user_attribute_filter_types: [
                  'string',
                  'advanced_filter_string'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Content Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Studio Name',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.studio_name',
                suggest_explore: 'client_crosstab',
                suggestable: true,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=233',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.studio_name ',
                sql_case: null,
                filters: null,
                times_used: 1390
              },
              {
                align: 'left',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: false,
                label: 'Content Details Title',
                label_from_parameter: null,
                label_short: 'Title',
                map_layer: null,
                name: 'client_crosstab.title',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'string',
                user_attribute_filter_types: [
                  'string',
                  'advanced_filter_string'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Content Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Title',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.title',
                suggest_explore: 'client_crosstab',
                suggestable: true,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=154',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.content_name ',
                sql_case: null,
                filters: null,
                times_used: 5215
              },
              {
                align: 'left',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: true,
                is_filter: false,
                is_numeric: false,
                label: 'Content Details Tv Show ID',
                label_from_parameter: null,
                label_short: 'Tv Show ID',
                map_layer: null,
                name: 'client_crosstab.tv_show_id',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'string',
                user_attribute_filter_types: [
                  'string',
                  'advanced_filter_string'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Content Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Tv Show ID',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.tv_show_id',
                suggest_explore: 'client_crosstab',
                suggestable: true,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=171',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.parent_content_id ',
                sql_case: null,
                filters: null,
                times_used: 4
              },
              {
                align: 'left',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: true,
                is_filter: false,
                is_numeric: false,
                label: 'Content Details Tv Show Name',
                label_from_parameter: null,
                label_short: 'Tv Show Name',
                map_layer: null,
                name: 'client_crosstab.tv_show_name',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'string',
                user_attribute_filter_types: [
                  'string',
                  'advanced_filter_string'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Content Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Tv Show Name',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.tv_show_name',
                suggest_explore: 'client_crosstab',
                suggestable: true,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=200',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.tv_show_name ',
                sql_case: null,
                filters: null,
                times_used: 7006
              },
              {
                align: 'right',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: true,
                is_filter: false,
                is_numeric: true,
                label: 'Content Details User ID',
                label_from_parameter: null,
                label_short: 'User ID',
                map_layer: null,
                name: 'client_crosstab.user_id',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'number',
                user_attribute_filter_types: [
                  'number',
                  'advanced_filter_number'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Content Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'User ID',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.user_id',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=159',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.user_id ',
                sql_case: null,
                filters: null,
                times_used: 0
              },
              {
                align: 'right',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: true,
                label: 'Engagement Actor ID',
                label_from_parameter: null,
                label_short: 'Actor ID',
                map_layer: null,
                name: 'pdt_character_vote_unified.actor_id',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'number',
                user_attribute_filter_types: [
                  'number',
                  'advanced_filter_number'
                ],
                value_format: null,
                view: 'pdt_character_vote_unified',
                view_label: 'Engagement',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'pdt_character_vote_unified',
                dimension_group: null,
                error: null,
                field_group_variant: 'Actor ID',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'pdt_character_vote_unified',
                suggest_dimension: 'pdt_character_vote_unified.actor_id',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/pdt_character_vote_unified.view.lkml?line=129',
                permanent: null,
                source_file: 'pdt_character_vote_unified.view.lkml',
                source_file_path:
                  'tvlytics/pdt_character_vote_unified.view.lkml',
                sql: '${TABLE}.actor_id ',
                sql_case: null,
                filters: null,
                times_used: 15
              },
              {
                align: 'left',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: false,
                label: 'Engagement Actor Name',
                label_from_parameter: null,
                label_short: 'Actor Name',
                map_layer: null,
                name: 'pdt_character_vote_unified.actor_name',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'string',
                user_attribute_filter_types: [
                  'string',
                  'advanced_filter_string'
                ],
                value_format: null,
                view: 'pdt_character_vote_unified',
                view_label: 'Engagement',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'pdt_character_vote_unified',
                dimension_group: null,
                error: null,
                field_group_variant: 'Actor Name',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'pdt_character_vote_unified',
                suggest_dimension: 'pdt_character_vote_unified.actor_name',
                suggest_explore: 'client_crosstab',
                suggestable: true,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/pdt_character_vote_unified.view.lkml?line=124',
                permanent: null,
                source_file: 'pdt_character_vote_unified.view.lkml',
                source_file_path:
                  'tvlytics/pdt_character_vote_unified.view.lkml',
                sql: '${TABLE}.actor_name ',
                sql_case: null,
                filters: null,
                times_used: 38
              },
              {
                align: 'left',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: false,
                label: 'Engagement Character Name',
                label_from_parameter: null,
                label_short: 'Character Name',
                map_layer: null,
                name: 'pdt_character_vote_unified.character_name',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'string',
                user_attribute_filter_types: [
                  'string',
                  'advanced_filter_string'
                ],
                value_format: null,
                view: 'pdt_character_vote_unified',
                view_label: 'Engagement',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'pdt_character_vote_unified',
                dimension_group: null,
                error: null,
                field_group_variant: 'Character Name',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'pdt_character_vote_unified',
                suggest_dimension: 'pdt_character_vote_unified.character_name',
                suggest_explore: 'client_crosstab',
                suggestable: true,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/pdt_character_vote_unified.view.lkml?line=134',
                permanent: null,
                source_file: 'pdt_character_vote_unified.view.lkml',
                source_file_path:
                  'tvlytics/pdt_character_vote_unified.view.lkml',
                sql: '${TABLE}.character_name ',
                sql_case: null,
                filters: null,
                times_used: 11
              },
              {
                align: 'left',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: false,
                label: 'Engagement Emotion Name',
                label_from_parameter: null,
                label_short: 'Emotion Name',
                map_layer: null,
                name: 'pdt_emotion_vote_unified.emotion_name',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'string',
                user_attribute_filter_types: [
                  'string',
                  'advanced_filter_string'
                ],
                value_format: null,
                view: 'pdt_emotion_vote_unified',
                view_label: 'Engagement',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'pdt_emotion_vote_unified',
                dimension_group: null,
                error: null,
                field_group_variant: 'Emotion Name',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'pdt_emotion_vote_unified',
                suggest_dimension: 'pdt_emotion_vote_unified.emotion_name',
                suggest_explore: 'client_crosstab',
                suggestable: true,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/pdt_emotion_vote_unified.view.lkml?line=41',
                permanent: null,
                source_file: 'pdt_emotion_vote_unified.view.lkml',
                source_file_path: 'tvlytics/pdt_emotion_vote_unified.view.lkml',
                sql: '${TABLE}.emotion_name ',
                sql_case: null,
                filters: null,
                times_used: 13
              },
              {
                align: 'left',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: 'range',
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: false,
                label: 'Series Details Episode First Air Date',
                label_from_parameter: null,
                label_short: 'Episode First Air Date',
                map_layer: null,
                name: 'client_crosstab.episode_first_air_date',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'date',
                user_attribute_filter_types: [
                  'datetime',
                  'advanced_filter_datetime'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Series Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Episode First Air Date',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.episode_first_air_date',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: true,
                can_time_filter: false,
                time_interval: { name: 'day', count: 1 },
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=255',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.episode_first_air_date ',
                sql_case: null,
                filters: null,
                times_used: 40
              },
              {
                align: 'right',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: true,
                label: 'Series Details Episode Number',
                label_from_parameter: null,
                label_short: 'Episode Number',
                map_layer: null,
                name: 'client_crosstab.episode_number',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'number',
                user_attribute_filter_types: [
                  'number',
                  'advanced_filter_number'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Series Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Episode Number',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.episode_number',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=183',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.episode_number ',
                sql_case: null,
                filters: null,
                times_used: 38
              },
              {
                align: 'right',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: true,
                label: 'Series Details Episode Sequence',
                label_from_parameter: null,
                label_short: 'Episode Sequence',
                map_layer: null,
                name: 'client_crosstab.episode_sort',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'number',
                user_attribute_filter_types: [
                  'number',
                  'advanced_filter_number'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Series Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Episode Sequence',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.episode_sort',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=281',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.episode_sort ',
                sql_case: null,
                filters: null,
                times_used: 28
              },
              {
                align: 'right',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: true,
                label: 'Series Details Season Number',
                label_from_parameter: null,
                label_short: 'Season Number',
                map_layer: null,
                name: 'client_crosstab.season_number',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'number',
                user_attribute_filter_types: [
                  'number',
                  'advanced_filter_number'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Series Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Season Number',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.season_number',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=177',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.season_number ',
                sql_case: null,
                filters: null,
                times_used: 45
              },
              {
                align: 'left',
                can_filter: true,
                category: 'dimension',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: false,
                label: 'Series Details Season | Episode',
                label_from_parameter: null,
                label_short: 'Season | Episode',
                map_layer: null,
                name: 'client_crosstab.episode_str',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'string',
                user_attribute_filter_types: [
                  'string',
                  'advanced_filter_string'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Series Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Season | Episode',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.episode_str',
                suggest_explore: 'client_crosstab',
                suggestable: true,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=288',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.episode_str ',
                sql_case: null,
                filters: null,
                times_used: 102
              }
            ],
            measures: [
              {
                align: 'right',
                can_filter: true,
                category: 'measure',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: true,
                is_filter: false,
                is_numeric: true,
                label: 'Content Details Binger Count',
                label_from_parameter: null,
                label_short: 'Binger Count',
                map_layer: null,
                name: 'client_crosstab.binger_count',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'sum_distinct',
                user_attribute_filter_types: [
                  'number',
                  'advanced_filter_number'
                ],
                value_format: '#,##0',
                view: 'client_crosstab',
                view_label: 'Content Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Binger Count',
                measure: true,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.binger_count',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=418',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.avg_user_weight',
                sql_case: null,
                filters: null,
                times_used: 0
              },
              {
                align: 'right',
                can_filter: true,
                category: 'measure',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: true,
                is_filter: false,
                is_numeric: true,
                label: 'Content Details Total Follow Count',
                label_from_parameter: null,
                label_short: 'Total Follow Count',
                map_layer: null,
                name: 'client_crosstab.total_follow_count',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'max',
                user_attribute_filter_types: [
                  'number',
                  'advanced_filter_number'
                ],
                value_format: '#,##0',
                view: 'client_crosstab',
                view_label: 'Content Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Total Follow Count',
                measure: true,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.total_follow_count',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=365',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.total_follow_count',
                sql_case: null,
                filters: null,
                times_used: 53
              },
              {
                align: 'right',
                can_filter: true,
                category: 'measure',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: true,
                is_filter: false,
                is_numeric: true,
                label: 'Content Details Weighted Engagement Count',
                label_from_parameter: null,
                label_short: 'Weighted Engagement Count',
                map_layer: null,
                name: 'client_crosstab.weighted_engagement_count',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'sum',
                user_attribute_filter_types: [
                  'number',
                  'advanced_filter_number'
                ],
                value_format: '#,##0',
                view: 'client_crosstab',
                view_label: 'Content Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Weighted Engagement Count',
                measure: true,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.weighted_engagement_count',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=404',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.weighted_engagement_count',
                sql_case: null,
                filters: null,
                times_used: 0
              },
              {
                align: 'right',
                can_filter: true,
                category: 'measure',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: true,
                label: 'Engagement Average Rating',
                label_from_parameter: null,
                label_short: 'Average Rating',
                map_layer: null,
                name: 'client_crosstab.average_rating',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'number',
                user_attribute_filter_types: [
                  'number',
                  'advanced_filter_number'
                ],
                value_format: '0.00',
                view: 'client_crosstab',
                view_label: 'Engagement',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Average Rating',
                measure: true,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.average_rating',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=344',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql:
                  'sum(${TABLE}.rating_5_total)/nullif(sum(${TABLE}.rating_5_count),0)',
                sql_case: null,
                filters: null,
                times_used: 613
              },
              {
                align: 'right',
                can_filter: true,
                category: 'measure',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: true,
                label: 'Engagement Character Vote Count',
                label_from_parameter: null,
                label_short: 'Character Vote Count',
                map_layer: null,
                name: 'client_crosstab.character_vote_count',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'sum',
                user_attribute_filter_types: [
                  'number',
                  'advanced_filter_number'
                ],
                value_format: '#,##0',
                view: 'client_crosstab',
                view_label: 'Engagement',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Character Vote Count',
                measure: true,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.character_vote_count',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=305',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.character_vote_count',
                sql_case: null,
                filters: null,
                times_used: 179
              },
              {
                align: 'right',
                can_filter: false,
                category: 'measure',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: true,
                label: 'Engagement Character Vote Share (Row)',
                label_from_parameter: null,
                label_short: 'Character Vote Share (Row)',
                map_layer: null,
                name: 'client_crosstab.character_vote_share',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'percent_of_total',
                user_attribute_filter_types: [],
                value_format: '0.00\\%',
                view: 'client_crosstab',
                view_label: 'Engagement',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Character Vote Share (Row)',
                measure: true,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.character_vote_share',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=320',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${character_vote_count}',
                sql_case: null,
                filters: null,
                times_used: 25
              },
              {
                align: 'right',
                can_filter: true,
                category: 'measure',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: true,
                label: 'Engagement Character Voter Count',
                label_from_parameter: null,
                label_short: 'Character Voter Count',
                map_layer: null,
                name: 'client_crosstab.character_voter_count',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'sum_distinct',
                user_attribute_filter_types: [
                  'number',
                  'advanced_filter_number'
                ],
                value_format: '#,##0',
                view: 'client_crosstab',
                view_label: 'Engagement',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Character Voter Count',
                measure: true,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.character_voter_count',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=312',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.avg_user_weight',
                sql_case: null,
                filters: null,
                times_used: 95
              },
              {
                align: 'right',
                can_filter: true,
                category: 'measure',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: true,
                label: 'Engagement Emotion Count',
                label_from_parameter: null,
                label_short: 'Emotion Count',
                map_layer: null,
                name: 'client_crosstab.emotion_count',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'sum',
                user_attribute_filter_types: [
                  'number',
                  'advanced_filter_number'
                ],
                value_format: '#,##0',
                view: 'client_crosstab',
                view_label: 'Engagement',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Emotion Count',
                measure: true,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.emotion_count',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=329',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.emotion_count',
                sql_case: null,
                filters: null,
                times_used: 267
              },
              {
                align: 'right',
                can_filter: true,
                category: 'measure',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: true,
                label: 'Engagement Emotion Voter Count',
                label_from_parameter: null,
                label_short: 'Emotion Voter Count',
                map_layer: null,
                name: 'client_crosstab.emotion_voter_count',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'sum_distinct',
                user_attribute_filter_types: [
                  'number',
                  'advanced_filter_number'
                ],
                value_format: '#,##0',
                view: 'client_crosstab',
                view_label: 'Engagement',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Emotion Voter Count',
                measure: true,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.emotion_voter_count',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=336',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.avg_user_weight',
                sql_case: null,
                filters: null,
                times_used: 180
              },
              {
                align: 'right',
                can_filter: true,
                category: 'measure',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: true,
                label: 'Performance Binge Rate',
                label_from_parameter: null,
                label_short: 'Binge Rate',
                map_layer: null,
                name: 'client_crosstab.binge_rate',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'number',
                user_attribute_filter_types: [
                  'number',
                  'advanced_filter_number'
                ],
                value_format: '0.00\\%',
                view: 'client_crosstab',
                view_label: 'Performance',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Binge Rate',
                measure: true,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.binge_rate',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=435',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '100*(${binger_count}/ NULLIF(${viewer_count},0))',
                sql_case: null,
                filters: null,
                times_used: 329
              },
              {
                align: 'right',
                can_filter: true,
                category: 'measure',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: true,
                label: 'Performance Engagement Score',
                label_from_parameter: null,
                label_short: 'Engagement Score',
                map_layer: null,
                name: 'client_crosstab.engagement_score',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'number',
                user_attribute_filter_types: [
                  'number',
                  'advanced_filter_number'
                ],
                value_format: '0.00',
                view: 'client_crosstab',
                view_label: 'Performance',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Engagement Score',
                measure: true,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.engagement_score',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=411',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql:
                  'coalesce( 30 * sqrt(${weighted_engagement_count} / NULLIF(${view_count},0)),0)',
                sql_case: null,
                filters: null,
                times_used: 901
              },
              {
                align: 'right',
                can_filter: true,
                category: 'measure',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: true,
                label: 'Performance Follow Count',
                label_from_parameter: null,
                label_short: 'Follow Count',
                map_layer: null,
                name: 'client_crosstab.follow_count',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'sum',
                user_attribute_filter_types: [
                  'number',
                  'advanced_filter_number'
                ],
                value_format: '#,##0',
                view: 'client_crosstab',
                view_label: 'Performance',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Follow Count',
                measure: true,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.follow_count',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=351',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.follow_count',
                sql_case: null,
                filters: null,
                times_used: 2211
              },
              {
                align: 'right',
                can_filter: true,
                category: 'measure',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: true,
                label: 'Performance Follow Growth Rate',
                label_from_parameter: null,
                label_short: 'Follow Growth Rate',
                map_layer: null,
                name: 'client_crosstab.follow_growth_rate',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'number',
                user_attribute_filter_types: [
                  'number',
                  'advanced_filter_number'
                ],
                value_format: '0.00\\%',
                view: 'client_crosstab',
                view_label: 'Performance',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Follow Growth Rate',
                measure: true,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.follow_growth_rate',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=372',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '100*(${follow_running_total} / ${total_follow_count})',
                sql_case: null,
                filters: null,
                times_used: 120
              },
              {
                align: 'right',
                can_filter: true,
                category: 'measure',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: true,
                label: 'Performance Follow Running Total',
                label_from_parameter: null,
                label_short: 'Follow Running Total',
                map_layer: null,
                name: 'client_crosstab.follow_running_total',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'average',
                user_attribute_filter_types: [
                  'number',
                  'advanced_filter_number'
                ],
                value_format: '#,##0',
                view: 'client_crosstab',
                view_label: 'Performance',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Follow Running Total',
                measure: true,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.follow_running_total',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=358',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.follow_run_tot',
                sql_case: null,
                filters: null,
                times_used: 79
              },
              {
                align: 'right',
                can_filter: true,
                category: 'measure',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: true,
                label: 'Performance View Count',
                label_from_parameter: null,
                label_short: 'View Count',
                map_layer: null,
                name: 'client_crosstab.view_count',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'sum',
                user_attribute_filter_types: [
                  'number',
                  'advanced_filter_number'
                ],
                value_format: '#,##0',
                view: 'client_crosstab',
                view_label: 'Performance',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'View Count',
                measure: true,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.view_count',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=379',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.view_count',
                sql_case: null,
                filters: null,
                times_used: 2893
              },
              {
                align: 'right',
                can_filter: false,
                category: 'measure',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: true,
                label: 'Performance View Share (col)',
                label_from_parameter: null,
                label_short: 'View Share (col)',
                map_layer: null,
                name: 'client_crosstab.view_share_col',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'percent_of_total',
                user_attribute_filter_types: [],
                value_format: '0.00\\%',
                view: 'client_crosstab',
                view_label: 'Performance',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'View Share (col)',
                measure: true,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.view_share_col',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=386',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${view_count} ',
                sql_case: null,
                filters: null,
                times_used: 7521
              },
              {
                align: 'right',
                can_filter: false,
                category: 'measure',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: true,
                label: 'Performance View Share (row)',
                label_from_parameter: null,
                label_short: 'View Share (row)',
                map_layer: null,
                name: 'client_crosstab.view_share_row',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'percent_of_total',
                user_attribute_filter_types: [],
                value_format: '0.00\\%',
                view: 'client_crosstab',
                view_label: 'Performance',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'View Share (row)',
                measure: true,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.view_share_row',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=395',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${view_count} ',
                sql_case: null,
                filters: null,
                times_used: 332
              },
              {
                align: 'right',
                can_filter: true,
                category: 'measure',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: true,
                label: 'Performance Viewer Count',
                label_from_parameter: null,
                label_short: 'Viewer Count',
                map_layer: null,
                name: 'client_crosstab.viewer_count',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'sum_distinct',
                user_attribute_filter_types: [
                  'number',
                  'advanced_filter_number'
                ],
                value_format: '#,##0',
                view: 'client_crosstab',
                view_label: 'Performance',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Viewer Count',
                measure: true,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.viewer_count',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=426',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${TABLE}.avg_user_weight',
                sql_case: null,
                filters: null,
                times_used: 1299
              },
              {
                align: 'right',
                can_filter: false,
                category: 'measure',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: true,
                label: 'Performance Viewer Share (col)',
                label_from_parameter: null,
                label_short: 'Viewer Share (col)',
                map_layer: null,
                name: 'client_crosstab.viewer_share_col',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'percent_of_total',
                user_attribute_filter_types: [],
                value_format: '0.00\\%',
                view: 'client_crosstab',
                view_label: 'Performance',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Viewer Share (col)',
                measure: true,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.viewer_share_col',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=442',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${viewer_count} ',
                sql_case: null,
                filters: null,
                times_used: 5857
              },
              {
                align: 'right',
                can_filter: false,
                category: 'measure',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: false,
                is_numeric: true,
                label: 'Performance Viewer Share (row)',
                label_from_parameter: null,
                label_short: 'Viewer Share (row)',
                map_layer: null,
                name: 'client_crosstab.viewer_share_row',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'percent_of_total',
                user_attribute_filter_types: [],
                value_format: '0.00\\%',
                view: 'client_crosstab',
                view_label: 'Performance',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Viewer Share (row)',
                measure: true,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.viewer_share_row',
                suggest_explore: 'client_crosstab',
                suggestable: false,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=451',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: '${viewer_count} ',
                sql_case: null,
                filters: null,
                times_used: 83
              }
            ],
            filters: [
              {
                align: 'left',
                can_filter: true,
                category: 'filter',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: true,
                is_numeric: false,
                label: 'Audience Sample Balance',
                label_from_parameter: null,
                label_short: 'Sample Balance',
                map_layer: null,
                name: 'client_crosstab.sample_balance',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'string',
                user_attribute_filter_types: [
                  'string',
                  'advanced_filter_string'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Audience',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Sample Balance',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.sample_balance',
                suggest_explore: 'client_crosstab',
                suggestable: true,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=129',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: null,
                sql_case: null,
                filters: null,
                times_used: 14
              },
              {
                align: 'left',
                can_filter: true,
                category: 'filter',
                default_filter_value: null,
                description: '',
                enumerations: null,
                field_group_label: null,
                fill_style: null,
                fiscal_month_offset: 0,
                has_allowed_values: false,
                hidden: false,
                is_filter: true,
                is_numeric: false,
                label: 'Content Details Genre Filter',
                label_from_parameter: null,
                label_short: 'Genre Filter',
                map_layer: null,
                name: 'client_crosstab.genre_filter',
                strict_value_format: false,
                requires_refresh_on_sort: false,
                sortable: true,
                suggestions: null,
                tags: [],
                type: 'string',
                user_attribute_filter_types: [
                  'string',
                  'advanced_filter_string'
                ],
                value_format: null,
                view: 'client_crosstab',
                view_label: 'Content Details',
                dynamic: false,
                week_start_day: 'monday',
                original_view: 'client_crosstab',
                dimension_group: null,
                error: null,
                field_group_variant: 'Genre Filter',
                measure: false,
                parameter: false,
                primary_key: false,
                project_name: 'tvlytics',
                scope: 'client_crosstab',
                suggest_dimension: 'client_crosstab.genre_filter',
                suggest_explore: 'client_crosstab',
                suggestable: true,
                is_fiscal: false,
                is_timeframe: false,
                can_time_filter: false,
                time_interval: null,
                lookml_link:
                  '/projects/tvlytics/files/client_crosstab.view.lkml?line=134',
                permanent: null,
                source_file: 'client_crosstab.view.lkml',
                source_file_path: 'tvlytics/client_crosstab.view.lkml',
                sql: null,
                sql_case: null,
                filters: null,
                times_used: 0
              }
            ],
            parameters: []
          }
        }
      ],
      availableFilters: {
        client_crosstab: {
          name: 'client_crosstab',
          filters: [
            'title',
            'contentType',
            'age',
            'country',
            'episode',
            'date',
            'gender',
            'genre',
            'studio',
            'network',
            'runtime',
            'season',
            'show',
            'panel',
            'originating_country',
            'first_release_date',
            'days_from_release',
            'metric'
          ]
        }
      }
    };
    return data;
  },

  fetchCustomView: async ({
    view,
    fields,
    filters,
    sorts,
    limit,
    pivots,
    total,
    row_total,
    resultFormat,
    applyFormatting
  }) => {
    try {
      const url = `${API_ROOT}v1/view/runStream`;
      const body = {
        view,
        fields,
        filters,
        sorts,
        limit,
        pivots,
        total,
        row_total,
        resultFormat
      };

      if (applyFormatting) {
        body.applyFormatting = true;
      }

      const overrides = {};

      if (resultFormat && resultFormat !== 'json') {
        overrides.headers = {
          'Content-Type': 'application/json',
          Accept: 'application/octet-stream'
        };

        return withAuth(apiCore.post)(url, JSON.stringify(body), overrides);
      }

      const { data } = await withAuth(apiCore.post)(
        url,
        JSON.stringify(body),
        overrides
      );

      return data;
    } catch (err) {
      if (err.status === 403) {
        return { url: '', blocked: true };
      }

      return Promise.reject(err);
    }
  },

  fetchReportList: async userId => {
    try {
      const url = `${API_ROOT}v1/custom-reports/${userId}`;
      const { data } = await withAuth(apiCore.get)(url);

      return data;
    } catch (err) {
      if (err.status === 403) {
        return { url: '', blocked: true };
      }

      return Promise.reject(err);
    }
  },

  fetchReport: async uuid => {
    try {
      const url = `${API_ROOT}v1/custom-report/${uuid}`;
      const { data } = await withAuth(apiCore.get)(url);

      return data;
    } catch (err) {
      if (err.status === 403) {
        return { url: '', blocked: true };
      }

      return Promise.reject(err);
    }
  },

  deleteReport: async (uuid, userId) => {
    try {
      const url = `${API_ROOT}v1/custom-report/${uuid}`;
      const body = {
        userId
      };
      const { data } = await withAuth(apiCore.del)(url, JSON.stringify(body));

      return data;
    } catch (err) {
      if (err.status === 403) {
        return { url: '', blocked: true };
      }

      return Promise.reject(err);
    }
  },

  updateReport: async (uuid, body) => {
    try {
      const url = `${API_ROOT}v1/custom-report/${uuid}`;
      const { data } = await withAuth(apiCore.put)(url, JSON.stringify(body));

      return data;
    } catch (err) {
      if (err.status === 403) {
        return { url: '', blocked: true };
      }

      return Promise.reject(err);
    }
  },

  createReport: async body => {
    try {
      const url = `${API_ROOT}v1/custom-report`;
      const { data } = await withAuth(apiCore.post)(url, JSON.stringify(body));

      return data;
    } catch (err) {
      if (err.status === 403) {
        return { url: '', blocked: true };
      }

      return Promise.reject(err);
    }
  },
  createBenchmarkRequest: async body => {
    try {
      const url = body.showId
        ? `${process.env.REACT_APP_PORTAL_API}v1/view/benchmark`
        : `${process.env.REACT_APP_PORTAL_API}v1/view/movie-benchmark`;
      const token = localStorage.getItem(TOKEN_STORAGE_KEY);
      const options = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/octet-stream'
        },
        method: 'POST',
        body: JSON.stringify(body)
      };

      const query = parseQuery(options);
      return fetch(`${url}${query}`, options);
    } catch (err) {
      console.log(err);
    }
  }
};
